<script lang="ts">
  // TrucoButton — botao "pedir TRUCO/SEIS/NOVE/DOZE" baseado no
  // valor atual da mao. Disabilita conforme regras.
  import type { MatchSnapshot } from '../../stores/match';
  import { nextEscalationWord } from '../../lib/cards';
  import { send } from '../../stores/ws';
  import { userId } from '../../stores/auth';

  type Props = { snap: MatchSnapshot };
  let { snap }: Props = $props();

  let nextWord = $derived(nextEscalationWord(snap.round_points || 1));
  let isMyTurn = $derived(snap.next_to_play_id === $userId);

  // myTeamSide: 'A' | 'B' | null. Em 1v1 retorna null (nao tem times).
  let myTeamSide = $derived.by<'A' | 'B' | null>(() => {
    if (snap.format !== '2v2') return null;
    if ((snap.team_a || []).some((p) => p.id === $userId)) return 'A';
    if ((snap.team_b || []).some((p) => p.id === $userId)) return 'B';
    return null;
  });

  // lastEscalatorTeam: time de quem foi o ultimo a escalar nessa mao
  // (A/B em 2v2, null em 1v1 ou se ninguem escalou). Bug Felipe 2026-05-26:
  // antes so checavamos pid individual, mas em 2v2 a regra eh que NENHUM
  // do mesmo time pode escalar consecutivo - se meu parceiro pediu TRUCO,
  // eu tambem nao posso pedir SEIS.
  let lastEscalatorTeam = $derived.by<'A' | 'B' | null>(() => {
    if (!snap.last_escalator_id || snap.format !== '2v2') return null;
    if ((snap.team_a || []).some((p) => p.id === snap.last_escalator_id)) return 'A';
    if ((snap.team_b || []).some((p) => p.id === snap.last_escalator_id)) return 'B';
    return null;
  });

  // Em 1v1: pode chamar se nao tem pending, nao locked, oponente foi
  // o ultimo a escalar (ou nenhum ainda).
  // Em 2v2: regra Leonardo — so a vez (NextToPlay) pode chamar. E o
  // mesmo TIME (nao so o pid individual) nao pode escalar 2x consecutivo.
  let canCall = $derived.by(() => {
    if (snap.status !== 'running') return false;
    if (snap.truco_challenge_running) return false;
    if (snap.truco_locked) return false;
    // 1v1: checa pid. 2v2: checa time (regra alternada inclui parceiro).
    if (snap.format === '2v2') {
      if (!isMyTurn) return false;
      if (lastEscalatorTeam && myTeamSide && lastEscalatorTeam === myTeamSide) return false;
    } else {
      if (snap.last_escalator_id === $userId) return false;
    }
    return true;
  });

  function handleCall() {
    if (!canCall) return;
    send('game:truco:call');
  }
</script>

<button
  type="button"
  class="truco-btn"
  class:disabled={!canCall}
  disabled={!canCall}
  onclick={handleCall}
>
  <span class="word">{nextWord}!</span>
</button>

<style>
  .truco-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #d850f0, #7a1d8e);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: var(--radius-md);
    color: #fff;
    font-weight: 900;
    font-size: 15px;
    letter-spacing: 2px;
    text-transform: uppercase;
    box-shadow:
      0 4px 14px rgba(216, 80, 240, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transition: transform 0.12s, box-shadow 0.15s;
    font-family: inherit;
  }
  .truco-btn:hover:not(.disabled):not(:disabled) {
    transform: translateY(-2px);
    box-shadow:
      0 6px 20px rgba(216, 80, 240, 0.55),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }
  .truco-btn:active:not(.disabled):not(:disabled) {
    transform: translateY(0) scale(0.97);
  }
  .truco-btn.disabled {
    background: linear-gradient(135deg, #3a2e4a, #1d1525);
    color: var(--text-dim);
    box-shadow: none;
  }
</style>
